import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, ImageIcon } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import WeeklyDaysPicker from "@/components/WeeklyDaysPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Settings() {
  const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => { if (data) setForm(data); }, [data, setForm]);

  const save = async () => {
    try {
      const payload = { ...form, default_grace_minutes: Number(form.default_grace_minutes) };
      delete payload._id;
      await api.put("/settings", payload);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setForm({ ...form, company_logo: reader.result }); setLogoPreview(reader.result); };
    reader.readAsDataURL(f);
  };

  const currentLogoSrc = logoPreview
    || (form.company_logo && form.company_logo.startsWith("data:") ? form.company_logo
    : form.company_logo ? fileUrl(form.company_logo) : null);

  return (
    <div className="space-y-5 max-w-2xl">
      <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Settings</h1>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
        Defaults apply only to NEW employees. Existing employees keep their individual settings.
      </div>

      <div className="bg-white rounded-2xl p-5 border border-slate-200 space-y-4">
        <div><label className="text-xs text-slate-500">Company Name</label><Input data-testid="company-name-input" value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} /></div>

        <div>
          <label className="text-xs text-slate-500">Company Logo (appears on payslips)</label>
          <div className="flex items-center gap-4 mt-1">
            <div className="h-16 w-16 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {currentLogoSrc ? <img src={currentLogoSrc} alt="logo" className="h-full w-full object-contain" data-testid="logo-preview" /> : <ImageIcon className="h-6 w-6 text-slate-300" />}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-logo-button"><Upload className="h-4 w-4 mr-1" /> Upload Logo</Button>
          </div>
        </div>

        <div><label className="text-xs text-slate-500">Timezone</label><Input data-testid="timezone-input" value={form.timezone || ""} onChange={(e) => set("timezone", e.target.value)} /></div>
        <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2">
          <div><label className="text-xs text-slate-500">Default Reporting Time</label><Input type="time" value={form.default_reporting_time || ""} onChange={(e) => set("default_reporting_time", e.target.value)} /></div>
          <div><label className="text-xs text-slate-500">Default Grace (min)</label><Input type="number" value={form.default_grace_minutes ?? ""} onChange={(e) => set("default_grace_minutes", e.target.value)} /></div>
          <div><label className="text-xs text-slate-500">Default Cutoff Time</label><Input type="time" value={form.default_cutoff_time || ""} onChange={(e) => set("default_cutoff_time", e.target.value)} /></div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Default Weekly Off (applies to new employees only)</label>
            <WeeklyDaysPicker value={form.default_weekly_leave || "sunday"} onChange={(v) => set("default_weekly_leave", v)} />
          </div>
        </div>
        <Button data-testid="save-settings-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save Settings</Button>
      </div>
    </div>
  );
}
